/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useMemo, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { di } from 'react-magnetic-di';

import {
	type FlexibleUiActionName,
	ActionName,
	InternalActionName,
	SmartLinkSize,
} from '../../../../../constants';
import { useFlexibleUiContext } from '../../../../../state/flexible-ui-context/useFlexibleUiContext';
import { useFlexibleUiOptionContext } from '../../../../../state/flexible-ui-context/useFlexibleUiOptionContext';
import Action from '../../actions/action';
import type { ActionMessage } from '../../actions/action/types';
import AISummaryAction from '../../actions/ai-summary-action';
import AutomationAction from '../../actions/automation-action';
import CopyLinkAction from '../../actions/copy-link-action';
import CustomUnresolvedAction from '../../actions/custom-unresolved-action';
import DeleteAction from '../../actions/delete-action';
import DownloadAction from '../../actions/download-action';
import EditAction from '../../actions/edit-action';
import FollowAction from '../../actions/follow-action';
import PreviewAction from '../../actions/preview-action';
import RovoChatAction from '../../actions/rovo-chat-action';
import ViewRelatedLinksAction from '../../actions/view-related-links-action';
import { ActionFooter } from './action-footer';
import { getPrimitivesPaddingSpaceBySize } from './getPrimitivesPaddingSpaceBySize';
import type { ActionBlockProps } from './types';

const ignoreContainerPaddingStyles = css({
	display: 'flex',
	flexDirection: 'column',
	boxSizing: 'border-box',
	flexGrow: 1,
	width: 'calc(100% + var(--container-gap-left) + var(--container-gap-right))',
	// We have to find a better way to ignore container padding
	// This has become more and more of a common use case.
	marginLeft: 'calc(var(--container-gap-left)  * -1)',
	marginRight: 'calc(var(--container-gap-right) * -1)',
});

const Actions = {
	[ActionName.AutomationAction]: AutomationAction,
	[ActionName.CopyLinkAction]: CopyLinkAction,
	[ActionName.CustomAction]: Action,
	[ActionName.DeleteAction]: DeleteAction,
	[ActionName.DownloadAction]: DownloadAction,
	[ActionName.EditAction]: EditAction,
	[ActionName.FollowAction]: FollowAction,
	[ActionName.PreviewAction]: PreviewAction,
	[ActionName.RovoChatAction]: RovoChatAction,
	[InternalActionName.AISummaryAction]: AISummaryAction,
	[InternalActionName.UnresolvedAction]: CustomUnresolvedAction,
	[InternalActionName.ViewRelatedLinksAction]: ViewRelatedLinksAction,
};

const DEFAULT_SORT_ORDER = ['PreviewAction', 'CopyLinkAction', 'AISummaryAction'];

const sort = (a: FlexibleUiActionName, b: FlexibleUiActionName) => {
	let idxA = DEFAULT_SORT_ORDER.indexOf(a);
	let idxB = DEFAULT_SORT_ORDER.indexOf(b);

	if (idxA === -1) {
		idxA = DEFAULT_SORT_ORDER.length;
	}

	if (idxB === -1) {
		idxB = DEFAULT_SORT_ORDER.length;
	}

	return idxA - idxB;
};

const ActionBlock = ({
	blockRef,
	onClick: onClickCallback,
	size,
	spaceInline,
	className,
	testId = 'smart-block-action',
}: ActionBlockProps): JSX.Element | null => {
	di(ActionFooter);

	const context = useFlexibleUiContext();
	const ui = useFlexibleUiOptionContext();

	const [message, setMessage] = useState<ActionMessage>();
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const onLoadingChange = useCallback(
		(isLoading: boolean) => {
			setIsLoading(isLoading);
		},
		[setIsLoading],
	);

	const padding = !ui?.hidePadding
		? getPrimitivesPaddingSpaceBySize(ui?.size || SmartLinkSize.Medium)
		: undefined;

	const onClick = useCallback(
		(name: FlexibleUiActionName) => {
			setMessage(undefined);
			onClickCallback?.(name);
		},
		[onClickCallback],
	);

	const onError = useCallback((error: ActionMessage) => {
		setMessage(error);
	}, []);

	const actions = useMemo(() => {
		if (!context?.actions) {
			return;
		}

		const arr = (Object.keys(context.actions) as FlexibleUiActionName[]).filter(
			(name) => name !== ActionName.RovoChatAction,
		);
		arr.sort(sort);

		const renderAction = (name: FlexibleUiActionName) => {
			const Action = name in Actions ? Actions[name as keyof typeof Actions] : undefined;
			if (!Action) {
				return null;
			}
			return (
				<Action
					as="stack-item"
					spaceInline={spaceInline}
					key={name}
					onClick={() => onClick(name)}
					onError={onError}
					onLoadingChange={onLoadingChange}
					size={size || ui?.size}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={padding ? { paddingInline: padding } : undefined}
					hideTooltip={isLoading}
				/>
			);
		};

		return arr.map((name) => renderAction(name));
	}, [
		context?.actions,
		spaceInline,
		onError,
		onLoadingChange,
		size,
		ui?.size,
		padding,
		isLoading,
		onClick,
	]);

	if (!actions) {
		return null;
	}

	return (
		<div
			css={[ignoreContainerPaddingStyles]}
			ref={blockRef}
			data-testid={testId}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className={className}
		>
			{actions}
			<ActionFooter message={message} testId={testId} />
		</div>
	);
};

export default ActionBlock;
